import isEmpty from 'lodash/isEmpty';

import { Project } from '@qality/common-model';
import {
  ProjectService,
  StorageOfUniqueServices,
  AxiosWrapperService as axios,
} from '@qality/common-service';
import { Utils } from '@qality/common-utils';

const REQUEST_CHUNK_SIZE = 10;
const SETTINGS_PROPERTY_KEY = 'soldevelo-qality-settings';

export default {
  async getSettings() {
    const allAdministeredProjects = await StorageOfUniqueServices.get(
      'AdministeredProjectService'
    ).getAllAdministeredProjects({ includeSettingsProperty: true });

    return allAdministeredProjects.map((project) => {
      const { settings, ...rest } = project;

      return new Project({
        ...rest,
        isAppVisibleInAllIssues: settings?.enabled ?? true,
        isPluginEnabled: settings?.isPluginEnabled ?? true,
      });
    });
  },

  async getProjectsAvailabilityByProjectKey(projects) {
    const projectsWithSettings = await this.getQAlitySettingsInBulk(projects);
    const projectsAvailabilityByProjectKey = {};
    projectsWithSettings.forEach((project) => {
      projectsAvailabilityByProjectKey[project.key] = project;
    });
    return projectsAvailabilityByProjectKey;
  },
  async getQAlitySettingsInBulk(projects) {
    const url = await StorageOfUniqueServices.get(
      'AuthenticateService'
    ).authenticateByJWT(
      StorageOfUniqueServices.get('WindowOriginService').origin +
        StorageOfUniqueServices.get('APIConstants').URL_PROJECTS_SETTINGS
    );
    const keys = projects.map((project) => project.key);
    const response = await axios.post(
      url,
      { keys: keys },
      {
        headers: {
          'Content-Type': 'application/json',
        },
      }
    );
    if (isEmpty(response.data)) {
      return [];
    }
    return response.data;
  },

  async getSettingsForProject(project) {
    return ProjectService.getProjectPropertyNoRetry(
      project.key,
      SETTINGS_PROPERTY_KEY
    )
      .then((response) => {
        const property = JSON.parse(response.body);
        return new Project({
          ...project,
          isAppVisibleInAllIssues: property.value.enabled,
          isPluginEnabled: !Utils.isNilOrEmpty(property.value.isPluginEnabled)
            ? property.value.isPluginEnabled
            : true,
        });
      })
      .catch(() => {
        return new Project({
          ...project,
          isAppVisibleInAllIssues: true,
          isPluginEnabled: true,
        });
      });
  },

  async updateQAlitySettings(projectWithSettings) {
    return ProjectService.setProjectPropertyAsUser(
      projectWithSettings.key,
      SETTINGS_PROPERTY_KEY,
      projectWithSettings.getRequestDataToUpdateServerSettings()
    );
  },

  async updateAppVisibilityInAllProjects(
    projects: Array<any>,
    isAppVisibilityInAllProjects: boolean
  ) {
    let promiseChain = Promise.resolve();
    for (let i = 0; i < projects.length; i += REQUEST_CHUNK_SIZE) {
      promiseChain = promiseChain.then(() =>
        Promise.all(
          projects.slice(i, i + REQUEST_CHUNK_SIZE).map((project) =>
            ProjectService.setProjectPropertyAsUser(
              project.key,
              SETTINGS_PROPERTY_KEY,
              {
                enabled: isAppVisibilityInAllProjects,
                isPluginEnabled: project.isPluginEnabled,
              }
            )
          )
        )
      );
    }
  },

  async updatePluginEnabledInAllProjects(
    projects: Array<any>,
    isPluginEnabled: boolean
  ) {
    let promiseChain = Promise.resolve();
    for (let i = 0; i < projects.length; i += REQUEST_CHUNK_SIZE) {
      promiseChain = promiseChain.then(() =>
        Promise.all(
          projects.slice(i, i + REQUEST_CHUNK_SIZE).map((project) =>
            ProjectService.setProjectPropertyAsUser(
              project.key,
              SETTINGS_PROPERTY_KEY,
              {
                isPluginEnabled: isPluginEnabled,
                enabled: project.isAppVisibleInAllIssues,
              }
            )
          )
        )
      );
    }
  },
};
